(ns hai.mini-apps.frame
  "Shared card frame for mini-app surfaces: a tinted header with an icon/title
   link and round action buttons, plus a placeholder body and the collapsible
   section used to group a card's rows. Each mini-app (notes, tasks, calendar)
   inherits this card in its own ns."
  (:require
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))
(def ^:private expand-icon (icons/mui-icon :outlined :unfold_more))
(def ^:private collapse-icon (icons/mui-icon :outlined :unfold_less))

(o/defstyled card :div
  {:background t/--card-surface
   :border-radius "16px"
   :box-shadow "0 2px 4px rgba(45,62,42,0.06), 0 12px 32px rgba(45,62,42,0.10)"
   :display "flex" :flex-direction "column"
   :position "relative"
   :color t/--card-forest}
  ;; Duck-tape strip overhanging the top edge, matching the clock / flow cards.
  ;; Each mini-app shifts it left/right via its own `::before`. (No `overflow:
  ;; hidden` here so the strip can overhang; the header rounds its own top
  ;; corners instead.)
  [:&::before
   {:content "''"
    :position "absolute"
    :top "-8px" :left "40px"
    :width "52px" :height "16px"
    :background "rgba(120,135,100,0.35)"
    :border-radius "2px"
    :box-shadow "0 1px 3px rgba(0,0,0,0.08)"}]
  ;; A card with action buttons is 28px of content tall, one with a ＋ pill
  ;; 34px, one without either only as tall as its title. The minimum holds
  ;; every card's header to the same height so they line up across a dashboard
  ;; row — and no vertical padding, which would add to the tallest control and
  ;; push a pill-bearing header past the others; the row centres its contents.
  [:.app-header
   {:display "flex" :align-items "center" :gap "10px"
    :padding "0 18px"
    :min-height "56px"
    :box-sizing "border-box"
    :border-top-left-radius "16px" :border-top-right-radius "16px"}]
  ;; Collapsed, the header is the whole card, so it rounds its bottom corners
  ;; too — the card is left unclipped for the duck tape.
  [:&.collapsed
   [:.app-header
    {:border-bottom-left-radius "16px" :border-bottom-right-radius "16px"}]]
  [:.app-icon
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   ;; The page applies `svg {fill: currentColor}`; these are stroke icons, so
   ;; restore fill: none or their shapes fill solid.
   [:svg {:fill "none"}]]
  [:.app-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0"
    :letter-spacing "-0.005em"
    :color t/--note-kraft}]
  ;; Icon + title as one link to the app's full page.
  [:.app-title-link
   {:display "inline-flex" :align-items "center" :gap "10px"
    :color "inherit"
    :text-decoration "none"
    :cursor "pointer"}
   [:&:hover
    [:.app-title {:text-decoration "underline" :text-underline-offset "3px"}]]]
  [:.app-actions
   {:margin-left "auto"
    :display "flex" :align-items "center" :gap "6px"}]
  [:.app-action-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color t/--note-kraft
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--note-kraft-10}]
   [:svg {:fill "currentColor"}]]
  [:.app-placeholder
   {:padding "32px 18px"
    :font-size "13px"
    :color t/--link-muted
    :text-align "center"}])

(defn collapse-toggle
  "The header's expand/collapse button. `class` is the host header's round
   button class: `app-action-btn` in a frame header, `bar-action-btn` in a
   notes or tasks bar. `label` names the card in the accessible name."
  [{:keys [collapsed? on-toggle label class]}]
  [:button
   {:type       "button"
    :class      class
    :aria-label (if collapsed? (str "Expand " label) (str "Collapse " label))
    :title      (if collapsed? "Expand" "Collapse")
    :on-click   on-toggle}
   (if collapsed? [expand-icon] [collapse-icon])])

;; ---------------------------------------------------------------------------
;; Collapsible section — groups a card's rows under a chevron/label/count toggle
;; ---------------------------------------------------------------------------

(o/defstyled section :div
  {:border-top "1px solid" :border-top-color t/--card-forest-10}
  [:&:first-child {:border-top "0"}]
  [:&:last-child
   {:overflow "hidden"
    :border-bottom-left-radius "16px"
    :border-bottom-right-radius "16px"}])

(o/defstyled section-toggle :button
  {:display "flex" :align-items "center" :gap "10px"
   :width "100%"
   :padding "14px 18px"
   :border "none" :background "transparent"
   :font-family "inherit"
   :text-align "left"
   :cursor "pointer"
   :transition "background 0.15s ease"}
  [:&:hover {:background t/--note-kraft-10}]
  [:.section-chevron
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   [:svg {:width "16px" :height "16px" :fill "currentColor"
          :transition "transform 0.15s ease"}]]
  [:&.open [:.section-chevron [:svg {:transform "rotate(90deg)"}]]]
  ;; Sized to match the notes card's eyebrow, so the two mini-apps' section
  ;; headers read as one system.
  [:.section-label
   {:font-size "11px" :font-weight "700"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--note-kraft}]
  [:.section-count
   {:font-size "11px" :font-weight "500"
    :color t/--link-muted}])

(o/defstyled section-empty :div
  {:padding "0 18px 14px"
   :font-size "13px"
   :color t/--link-muted})

(defn section-view
  "A collapsible section: a chevron + uppercase LABEL + count toggle over
   `body`, shown only when `open?`. `on-toggle` flips it; an absent/nil `body`
   shows `empty-label` (default \"Nothing here.\")."
  [{:keys [label item-count open? on-toggle empty-label]} body]
  [section
   [section-toggle
    {:type "button"
     :class (when open? "open")
     :on-click on-toggle}
    [:span.section-chevron [chevron-icon]]
    [:span.section-label label]
    [:span.section-count item-count]]
   (when open?
     (or body [section-empty (or empty-label "Nothing here.")]))])

;; ---------------------------------------------------------------------------
;; List chrome — the scroll column a card's rows stack in, its clip, fade and
;; footer
;; ---------------------------------------------------------------------------

;; The list's scroll column under the header: rows stacked in the card,
;; scrolling inside it instead of stretching it. Each mini-app derives its own
;; to size its rows.
;; The scrollbar is styled so that it shows whenever the list overflows: an
;; unstyled one is the browser's, drawn or hidden at idle by the OS and even
;; by the tab. Standard props only — a scrollbar-color inherited from a host's
;; scroll column makes Chrome ignore ::-webkit-scrollbar rules. Two tokens in
;; one value can't be interpolated in a pure-cljs defstyled, so they're named
;; by hand.
(o/defstyled list-body :div
  {:display "flex" :flex-direction "column" :gap "10px"
   :padding "12px 14px 16px"
   :flex "1 1 auto" :min-height "0"
   :overflow-y "auto"
   :overflow-x "hidden"
   :scrollbar-width "thin"
   :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"}
  [:.empty
   {:padding "18px 4px 8px"
    :font-size "13px"
    :color t/--link-muted
    :text-align "center"
    :font-style "italic"}]
  [:.loading
   {:display "flex" :justify-content "center"
    :padding "18px 0"}])

;; Rounds the scrollbar's end into the card's bottom corners. Chrome clips a
;; scrollbar by an ancestor's overflow clip, never by the scroller's own
;; border-radius; the sidebar clips its whole card, but on Home the frame's
;; duck-tape strip overhangs it, so the clip sits here, around the list alone.
;; The fade positions against this box, whose bottom is the card's.
(o/defstyled list-clip :div
  {:position "relative"
   :display "flex" :flex-direction "column"
   :flex "1 1 auto" :min-height "0"
   :overflow "hidden"
   :border-bottom-left-radius "16px"
   :border-bottom-right-radius "16px"}
  ;; Unpositioned, so what is laid over the body is laid over the whole card,
  ;; header and all, rather than clipped to the body.
  [:&.card-wide-overlays {:position "static"}])

;; Smooths the scroll cutoff: rows clipped by the scrollport dissolve into the
;; tray just above the card's rounded bottom edge. Positioned against the
;; card, so it sits beside the list body rather than inside its scrollport.
;; The transparent end restates --card-surface's value by hand (no str
;; interpolation in a pure cljs defstyled).
(o/defstyled bottom-fade :div
  {:position "absolute"
   :left "0" :right "0" :bottom "0"
   :height "26px"
   :background "linear-gradient(to bottom, rgba(247,246,239,0), var(--card-surface))"
   :border-bottom-left-radius "16px"
   :border-bottom-right-radius "16px"
   :pointer-events "none"
   :z-index "1"})

;; Under the last row: that the list is cut at the latest few, once it is,
;; and the way to the rest — `noun` names the rows, plural. The bottom padding
;; keeps it clear of the fade once the list is scrolled to its end.
(o/defstyled list-footer :div
  {:display "flex" :flex-direction "column" :align-items "center" :gap "4px"
   :padding "10px 4px 18px"
   :border-top-width "1px" :border-top-style "dashed" :border-top-color t/--note-sage-35
   :font-size "12px"
   :color t/--link-muted}
  [:.see-all-link
   {:color t/--note-kraft
    :font-weight "600"
    :text-decoration "underline"
    :text-underline-offset "3px"}
   [:&:hover {:color t/--card-forest}]]
  ([{:keys [item-count limit noun see-all]}]
   [:<>
    (when (>= item-count limit)
      [:span (str "Showing your latest " limit " " noun)])
    [:a.see-all-link see-all (str "See all " noun)]]))
